import * as React from 'react';

import CookiesList, { ICookiesListStateProps } from './CookiesList';

import './CookiesList.scss';

export interface ICookiePolicyContentStateProps
  extends ICookiesListStateProps {}

export interface ICookiePolicyContentDispatchProps {
  openCookieSettings: () => void;
}

const CookiePolicyContent: React.FC<
  ICookiePolicyContentDispatchProps & ICookiePolicyContentStateProps
> = ({ openCookieSettings, html }) => {
  const cookieSettingsButton = (
    <button
      className='btn btn-primary btn-sm mb-3'
      onClick={openCookieSettings}
    >
      Cookie Settings
    </button>
  );

  const cookieConsentManagerLink = (
      /* eslint-disable-next-line */
      <a className='policy' onClick={openCookieSettings}>Cookie Consent Manager</a>
  );

  return (
    <div className='policy'>
      <h2 className='text-center'>
        <strong>Cookie Policy</strong>
      </h2>

      <p className='text-center'>Last Updated: August 2023</p>

      <p><strong>Below is information about how Adaptive Biotechnologies
        Corporation uses cookies and similar technology.</strong></p>
      <p>This applies to the Adaptive Biotechnologies Corporation websites
        that display this Cookie Policy (collectively, the “Site”). The Site
        uses cookies, in combination with pixels, local storage objects, and
        similar devices (collectively, “cookies” unless otherwise noted) to
        distinguish you from other users of our Site. This helps us to provide
        you with a good experience when you browse our Site, improve our Site,
        and to personalize ads and content to you while you use our Site.
        This provides you with information about the types of cookies we use and
        the purposes for using those cookies.</p>
      <p>
        <strong>1. &nbsp;<u>What is a Cookie?</u></strong>
      </p>
      <blockquote>
        <p>A cookie is a small file with a unique identifier that is transferred
          to your browser through our Site. Cookies allow us to remember users who
          are logged in, to understand how users navigate through and use our Site
          and the information and services we make available through our Site
          (collectively, our “Services”), to improve and measure our advertising
          campaigns (including on third-party sites and applications), and to
          secure and protect our Site and Services.</p>
      </blockquote>

      <p>
        <strong>2. &nbsp;<u>How can I change my cookie preferences? </u></strong>
      </p>

      <blockquote>
        <p>Your cookie preferences are set on a browser and device basis. You
          may opt out of certain cookies on our Sites using our cookie settings
          tool, as well as by adjusting your browser settings. You can review or
          change your preferences for most third-party cookies and tags on our
          websites by adjusting your cookie settings below. If you are in
          California, you may also change your preferences via the “Do Not Sell or
          Share My Personal Information” link in the footer of our Site.</p>
        <p>Some browsers and tools may include a “Global Privacy Control” to
          enable California residents to communicate their “Do Not Sell or Share
          My Personal Information” preferences to websites, under the CCPA. If our
          Site recognizes that your browser is communicating a “Do Not Sell or
          Share My Personal Information” preference, our Cookie Settings tool will
          process this as an opt out of performance, targeting and functional
          cookies on our Site.</p>
        <p>Please note that, whether you use our Cookie Settings tool or other
          cookie control tools, you will have to change your settings (or set GPC)
          for each browser and device you use. Also, please be aware that if you
          do not want to accept any cookies, we cannot guarantee that our services
          will function properly. It may be that several functions will be
          unavailable to you or that you will even be unable to view certain parts
          of the Site.</p>
      </blockquote>
      <p><strong><u>Cookie Settings Tool.</u></strong></p>
      <p>You can review or change your preferences for most third-party
        cookies and tags on our websites by adjusting your cookie settings:</p>
      <p>{cookieSettingsButton}</p>
      <p>Please note, that your preferences are applied on a browser and
        device basis. So, you will need to set your cookie preferences for each
        browser and device you use to access our Site. Further, if you
        subsequently delete cookies, your preferences may be lost and need to be
        reset.</p>

      <p>
        <strong>3. &nbsp;<u>How can I delete and/or block cookies on my
          browser?</u></strong>
      </p>

      <blockquote>
        <p>You may delete, review and block cookies by adjusting your browser
          settings. However, if you block all cookies (including essential
          cookies), you may not be able to access all or parts of our Site.
          Please see the links below for more information on how to manage cookie
          settings for certain bowsers:</p>
      </blockquote>
      <ul>
        <li><p>Firefox | “Private Browsing” and managing cookie settings: <a
            href="https://support.mozilla.org/en-US/kb/enable-and-disable-cookies-website-preferences">learn
          more</a></p></li>
        <li><p>Chrome | “Incognito” mode and managing cookie settings: <a
            href="http://support.google.com/chrome/bin/answer.py?hl=en&amp;answer=95647">learn
          more</a></p></li>
        <li><p>Internet Explorer | “InPrivate” mode and managing cookie
          settings: <a
              href="http://windows.microsoft.com/en-us/internet-explorer/delete-manage-cookies">learn
            more</a></p></li>
        <li><p>Safari | “Private Browsing” and managing cookie settings: <a
            href="https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac">learn
          more</a></p></li>
      </ul>
      <p>For further information about cookies, including how to see what
        cookies have been set and how to manage and delete them, please
        visit: <a
            href="http://www.allaboutcookies.org/"><em>www.allaboutcookies.org</em></a>.</p>

      <p>
        <strong>4. &nbsp;<u>How can I change cookie settings on my mobile device?</u></strong>
      </p>

      <blockquote>
        <p>You can review and change permissions regarding targeted advertising
          by adjusting your device privacy settings. This means that you will no
          longer get targeted ads; however, you may still receive contextual or
          general ads.</p>
        <p>Most device and smart phone operating systems (e.g., iOS, Android,
          Windows) will have settings on how to manage privacy preferences,
          including on how to prevent the delivery of tailored advertising and
          tracking. For example, iOS users can deny apps permission to track your
          activities across apps and websites on a mobile device in the device
          settings. You should refer to your device manufacturer’s most recent
          instructions on how to restrict tracking, reset advertising identifiers,
          and/or opt out of targeted advertising all together. Note that choosing
          to reset your advertising identifier on Android phones is much like
          deleting cookies in a browser—the device is harder to associate with
          past activity, but tracking may still occur using the new advertising
          identifier.</p>
      </blockquote>

      <p>
        <strong>5. &nbsp;<u>Do you use third-party cookies from other companies or
          ad networks?</u></strong>
      </p>

      <p>We may send users targeted ads, based on their location and
        activities on our Site and in order to do so we may allow third parties
        to use cookies to collect usage information on our Sites and display
        targeted ads and content to you on our behalf; these third parties may
        be able to associate the information they collect on our Sites with
        other information they have about you from other sources. (Please see
        our cookie list below, for a list of third parties.) This enables us
        and these third parties to target advertisements by displaying ads for
        products and services in which you might be interested and to measure
        and improve the effectiveness of targeted ads. These third-party cookies
        and other technologies are governed by each third party’s specific
        privacy policy, not this one. <strong>You may opt out of Targeting (and
          other cookies) on our Sites as described above.</strong></p>
      <p>For more information about how third-party cookies are used for
        targeted advertising and how to opt out of certain third-party
        advertising related cookies, please go to these self-regulatory
        advertising industry organization websites:</p>
      <ul>
        <li><p>U.S.: <a
            href="http://optout.aboutads.info/?c=2&amp;lang=EN">Digital Advertising
          Alliance’s Consumer Choice Page</a> and <a
            href="http://optout.networkadvertising.org/?c=1#completed">Network
          Advertising Initiative Consumer Opt-Out</a></p></li>
        <li><p>Canada: <a href="https://youradchoices.ca/choices/">Digital
          Advertising Alliance of Canada’s Opt-Out Tool</a></p></li>
        <li><p>European Union: <a
            href="http://www.youronlinechoices.eu/">European Interactive Digital
          Advertising Alliance</a></p></li>
      </ul>
      <p><strong><br/>
      </strong>We use third-party analytics tools, including Google Analytics
        and others as set forth in our cookie list below, to collect, monitor
        and analyze this information in order to improve functionality and
        user-friendliness, and to better tailor the Services to our visitors’
        needs. Accordingly, usage data is shared with <a
            href="http://www.google.com/analytics/learn/privacy.html?rd=2">Google</a>,
        or other third parties, each of which have their own privacy policy
        addressing how they handle such information and any choices you may have
        about their collection and use of information. We recommend that you
        review these privacy policies.</p>

      <p>
        <strong>6. &nbsp;<u>How do we use Google Analytics?</u></strong>
      </p>


      <p>For more information about Google Analytics cookies, please see
        Google’s <a
            href="https://support.google.com/analytics/?hl=en#topic=3544906">help
          pages</a> and <a
            href="https://policies.google.com/privacy?hl=en">privacy policy</a>.
        Google has developed the Google Analytics opt-out browser add-on; if you
        want to opt out of Google Analytics, you can download and install the
        add-on for your web browser <a
            href="https://tools.google.com/dlpage/gaoptout?hl=en-GB">here</a>.</p>
      <p>Google Analytics is a web analytics service provided by Google, Inc.
        (“Google”). Google uses cookies to help us analyze how users use our
        Site. The information generated by the cookie about your use of one of
        our Sites (including your IP address) will be transmitted to and stored
        by Google on servers in the United States. In case of activation of the
        IP anonymization feature, Google will truncate/anonymize the last octet
        of the IP address for Member States of the European Union as well as for
        other parties to the Agreement on the European Economic Area. Only in
        exceptional cases, the full IP address is sent to and shortened by
        Google servers in the United States. On behalf of our Site, Google will
        use this information for the purpose of evaluating your use of the Site,
        compiling reports on your activity for us and third parties who help
        operate and provide services related to the Site. Google will not
        associate your IP address with any other data held by Google. You may
        refuse the use of these cookies by selecting the appropriate settings on
        your browser as discussed in this Cookie Policy. However, please note
        that if you do this, you may not be able to use the full functionality
        of the Site. Furthermore you can prevent collection and use of data
        (cookies and IP address) by Google Analytics by downloading and
        installing the browser plug-in available under <a
            href="https://tools.google.com/dlpage/gaoptout?hl=en-GB">https://tools.google.com/dlpage/gaoptout?hl=en-GB</a>.
      </p>
      <p>Further information concerning the terms and conditions of use and
        data privacy can be found at <a
            href="http://www.google.com/analytics/terms/gb.html">http://www.google.com/analytics/terms/gb.html</a> or
        at <a
            href="https://www.google.de/intl/en_uk/policies/">https://www.google.de/intl/en_uk/policies/</a>.</p>
      <p>Please note that on the Site, Google Analytics code is supplemented
        by “gat._anonymizeIp();” to ensure an anonymized collection of IP
        addresses (also called IP-masking).</p>

      <p>
        <strong>7. &nbsp;<u>What types of cookies does this Site
          use?</u></strong>
      </p>

      <p>The cookies used on our sites have been categorized in the table
        below. However, it is important to note that not all cookies may be used
        for all Sites. You can see the precise cookie categories and cookies
        implemented on each Site by viewing the cookie list in section 8.</p>
      <table>
        <colgroup>
          <col width = "42%"/>
          <col width = "57%"/>
        </colgroup>
        <thead>
        <tr>
          <th><strong>Category</strong></th>
          <th><strong>Description</strong></th>
        </tr>
        </thead>
        <tbody>
        <tr className="odd">
          <td><em><strong>Strictly Necessary Cookies</strong></em></td>
          <td>These cookies are necessary for our Site to function properly and
            cannot be switched off in our systems. Without these cookies, we cannot
            respond to your requests or provide you with the services you request or
            access on our Site. For example, we use these cookies to do the
            following: to remember information you have entered on forms during a
            web browser session; to identify you as being logged into our Site; for
            security purposes; and to route users to specific applications or
            specific servers. You can set your browser to block or alert you about
            these cookies, but if you do so, some parts of the site will not
            function properly.
          </td>
        </tr>
        <tr className="even">
          <td><em><strong>First and Third-Party Cookies</strong></em></td>
          <td>We use both first-party and third-party cookies on our Site. Whether
            a cookie is ‘first’ or ‘third’ party refers to the domain placing the
            cookie. First-party cookies are those set by a website that is being
            visited by the user at the time (e.g., cookies placed by
            adaptivebiotech.com). Third-party cookies are cookies that are set by a
            domain other than that of the website being visited by the user. If a
            user visits a website and another entity sets a cookie through that
            website, this would be a third-party cookie.
          </td>
        </tr>
        <tr className="odd">
          <td><em><strong>Performance Cookies</strong></em></td>
          <td> These cookies collect information about how you use our Site –
            e.g., which pages you visit and if you experience any errors. They allow
            us to count visits and traffic sources so we can measure and improve the
            performance of our Site. For example, they help us to know which pages
            are the most and least popular and see how visitors move around the
            site. Some of our performance cookies are managed for us by third
            parties. If you do not allow these cookies we will not know when you
            have visited our site and will not be able to monitor its
            performance.
          </td>
        </tr>
        <tr className="even">
          <td><em><strong>Functional Cookies</strong></em></td>
          <td>These cookies enable our Site to provide enhanced functionality and
            personalization. For example, we use these cookies to remember settings
            you’ve applied, such as language, to provide and show embedded videos
            and content, and to make available certain other features. Some of these
            cookies are managed for us by third parties, including by third party
            providers whose services we have added to our pages. If you do not allow
            these cookies, then some or all of these services may not function
            properly.
          </td>
        </tr>
        <tr className="odd">
          <td><em><strong>Session Cookies</strong></em></td>
          <td>These cookies allow website operators to link the actions of a user
            during a browser session. A browser session starts when a user opens the
            browser window and finishes when they close the browser window. Session
            cookies are created temporarily. Once you close the browser, all session
            cookies are deleted.
          </td>
        </tr>
        <tr className="even">
          <td><em><strong>Persistent Cookies</strong>.</em></td>
          <td>These cookies remain on a user’s device for the period of time
            specified in the cookie. They are activated each time that the user
            visits the website that created that particular cookie.
          </td>
        </tr>
        <tr className="odd">
          <td><em><strong>Targeting Cookies</strong></em>.</td>
          <td>These are third-party cookies that allow us to better reach you with
            relevant ads and content—both on our Site and on third-party websites,
            apps and online services—and to measure and improve our advertising and
            marketing. These cookies enable third-party ad companies to track your
            visits to the Site, as well as other websites, apps and online services,
            which they use to understand your interests and activities in order to
            provide you more relevant ads on behalf of us and other companies. If
            you do not allow these cookies, you will still see ads online, but they
            may be less relevant to you.
          </td>
        </tr>
        <tr className="even">
          <td><em><strong>Social Media Cookies</strong></em></td>
          <td>These cookies are set by a range of social media services that we
            have added to the site to enable you to share our content with your
            friends and networks. Similar to targeting cookies, they may enable
            these third-party companies to track your track visits to the Site, as
            well as other websites, apps and online services, in order to understand
            your browsing activities and interests. Some of these third-party social
            media companies may make available certain privacy options to you with
            respect to their services and information collection; you should check
            their respective privacy policies for more information. If you do not
            allow these cookies, you may not be able to use or see these sharing
            tools on our Site. You can update your preferences regarding
              Social Media Cookies by adjusting your preferences on Targeting
              Cookies.</td>
        </tr>
        </tbody>
      </table>

      <p>
        <strong>8. &nbsp;<u>What cookies are used?</u></strong>
      </p>

      <p><CookiesList html={html} /></p>

      <p>
        <strong>9. &nbsp;<u>How do I get more information?</u></strong>
      </p>

      <p>If you have any questions, please contact <a
          href="mailto:privacy@adaptivebiotech.com">privacy@adaptivebiotech.com</a>.
        You can adjust your cookie settings at any time from our {cookieConsentManagerLink}.
      </p>
    </div>
  );
};

export default CookiePolicyContent;
